import http from "../../../config/BaseUrl";
import { DEFAULT_INITIAL_MESSAGE_SEND_TIME } from "./automationConstants";

export const SMS_AUTOMATION_TEMPLATE_ENDPOINT = "automation/templates";
export const SMS_AUTOMATION_FLOW_ENDPOINT = "automation/flows";
export const SMS_CONVERSATION_ENDPOINT = "automation/conversations";
export const SMS_BILLING_ENDPOINT = "automation/sms";
export const SMS_BILLING_OVERVIEW_ENDPOINT = `${SMS_BILLING_ENDPOINT}/dashboard`;
export const SMS_BILLING_SUMMARY_ENDPOINT = `${SMS_BILLING_ENDPOINT}/billing-summary`;
export const SMS_BILLING_RECORDS_ENDPOINT = `${SMS_BILLING_ENDPOINT}/billing-records`;
export const CONVERSIONS_MONTHLY_OVERVIEW_ENDPOINT = "automation/conversions/monthly-overview";
export const SMS_USAGE_LOGS_ENDPOINT = `${SMS_BILLING_ENDPOINT}/usage-logs`;
export const SMS_CREDIT_BALANCE_ENDPOINT = `${SMS_BILLING_ENDPOINT}/credit-balance`;
export const SMS_CREDIT_TRANSACTIONS_ENDPOINT = `${SMS_BILLING_ENDPOINT}/credit-transactions`;
export const SMS_DELIVERY_REPORTS_ENDPOINT = `${SMS_BILLING_ENDPOINT}/delivery-reports`;
export const LIVE_EVENT_ENDPOINT = "live-event";
export const AUTOMATION_CONVERSATION_EVENTS_ENDPOINT = "events";
export const SMS_NOTIFICATION_POLL_INTERVAL = 60000;
export const SMS_NOTIFICATIONS_UPDATED_EVENT = "sms-notifications-updated";

const withoutLifecycleFields = (payload = {}) => {
  const editablePayload = { ...payload };

  delete editablePayload.is_active;
  delete editablePayload.isActive;
  delete editablePayload.active;
  delete editablePayload.status;

  return editablePayload;
};

export const SMS_NOTIFICATION_TYPE_LABELS = {
  customer_reply: "Customer Reply",
  reminder: "Reminder Due",
  automation: "Automation Executed",
  delivery_failed: "Delivery Failed",
  delivery_success: "Delivery Success",
  delivery_report: "Delivery Report",
  credit_warning: "SMS Credit Warning",
  system: "System",
};

export const readFirstDefined = (source, keys) => {
  if (!source) {
    return null;
  }

  const key = keys.find((item) => source[item] !== undefined && source[item] !== null);

  return key ? source[key] : null;
};

export const asArray = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value.data)) {
    return value.data;
  }

  return [];
};

export const normaliseCollection = (payload, keys = []) => {
  const source = payload?.data || payload || {};
  const collection =
    keys.map((key) => source[key]).find(Boolean) ||
    source.data ||
    (Array.isArray(source) ? source : []);
  const items = asArray(collection);

  return {
    data: items,
    currentPage: collection.current_page || source.current_page || 1,
    total: collection.total || source.total || items.length,
    perPage: collection.per_page || source.per_page || items.length || 10,
    from: collection.from || source.from || (items.length ? 1 : 0),
    to: collection.to || source.to || items.length,
  };
};

export const normaliseSmsNotification = (notification, index = 0) => {
  const customer = readFirstDefined(notification, ["customer", "contact", "guest", "lead"]) || {};
  const event = readFirstDefined(notification, ["event", "event_details", "eventDetails"]) || {};
  const flow = readFirstDefined(notification, ["flow", "automation_flow", "automationFlow"]) || {};
  const rawType =
    readFirstDefined(notification, ["type", "notification_type", "notificationType", "category"]) ||
    (readFirstDefined(notification, ["conversation_id", "conversationId", "unread_count", "unreadCount"]) !== null
      ? "customer_reply"
      : "system");
  const type = String(rawType).replace(/-/g, "_").toLowerCase();
  const unreadCount = Number(
    readFirstDefined(notification, ["unread_count", "unreadCount", "unread_messages", "unreadMessages"]) || 0
  );
  const readAt = readFirstDefined(notification, ["read_at", "readAt"]);
  const status = readFirstDefined(notification, ["status", "state"]);
  const isRead = readFirstDefined(notification, ["is_read", "isRead", "read"]);
  const customerName =
    readFirstDefined(notification, ["customer_name", "customerName", "contact_name", "contactName", "name"]) ||
    readFirstDefined(customer, ["name", "full_name", "fullName", "customer_name", "customerName"]);
  const eventName =
    readFirstDefined(notification, ["event_name", "eventName"]) ||
    readFirstDefined(event, ["name", "event_name", "eventName", "title"]);
  const flowName =
    readFirstDefined(notification, ["automation_flow_name", "automationFlowName", "flow_name", "flowName"]) ||
    readFirstDefined(flow, ["name", "flow_name", "flowName", "title"]);
  const message =
    readFirstDefined(notification, [
      "message",
      "body",
      "description",
      "last_message",
      "lastMessage",
      "last_message_preview",
      "lastMessagePreview",
      "preview",
    ]) || (type === "customer_reply" ? "Customer replied to a venue SMS conversation." : "No message available.");
  const timestamp = readFirstDefined(notification, [
    "timestamp",
    "created_at",
    "createdAt",
    "sent_at",
    "sentAt",
    "due_at",
    "dueAt",
    "last_reply_at",
    "lastReplyAt",
    "last_inbound_at",
    "lastInboundAt",
    "last_message_at",
    "lastMessageAt",
    "last_activity_at",
    "lastActivityAt",
    "updated_at",
    "updatedAt",
  ]);
  const conversationId = readFirstDefined(notification, ["conversation_id", "conversationId", "id", "uuid"]);
  const flowId =
    readFirstDefined(notification, ["automation_flow_id", "automationFlowId", "flow_id", "flowId"]) ||
    readFirstDefined(flow, ["id", "flow_id", "flowId"]);
  const reminderId = readFirstDefined(notification, ["reminder_id", "reminderId"]);
  const eventId =
    readFirstDefined(notification, ["event_id", "eventId"]) ||
    readFirstDefined(event, ["id", "event_id", "eventId"]);

  return {
    ...notification,
    id:
      readFirstDefined(notification, [
        "notification_id",
        "notificationId",
        "id",
        "conversation_id",
        "conversationId",
        "uuid",
      ]) || `${type}-${index}`,
    title:
      readFirstDefined(notification, ["title", "subject", "heading"]) ||
      (type === "customer_reply" && customerName
        ? `New customer reply from ${customerName}`
        : SMS_NOTIFICATION_TYPE_LABELS[type] || SMS_NOTIFICATION_TYPE_LABELS.system),
    message,
    type,
    eventName,
    customerName,
    conversationId,
    flowId,
    flowName,
    reminderId,
    eventId,
    timestamp,
    unread: unreadCount > 0 || isRead === false || isRead === 0 || isRead === "0" || (!readAt && status === "unread"),
    unreadCount,
    automationAction: readFirstDefined(notification, ["automation_action", "automationAction", "last_action", "lastAction"]),
  };
};

export const normaliseSmsNotificationsResponse = (payload) => {
  const collection = normaliseCollection(payload, [
    "notifications",
    "sms_notifications",
    "smsNotifications",
    "conversations",
    "sms_conversations",
    "smsConversations",
  ]);

  return {
    ...collection,
    data: collection.data
      .map(normaliseSmsNotification)
      .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()),
  };
};

export const normaliseSmsNotificationSummary = (payload) => {
  const source = payload?.data || payload || {};
  const summary =
    source.summary ||
    source.notifications ||
    source.notification_summary ||
    source.notificationSummary ||
    source.overview ||
    source;
  const summarySource = Array.isArray(summary) ? summary[0] || {} : summary;

  return {
    unreadNotifications: readFirstDefined(summarySource, [
      "unread_notifications",
      "unreadNotifications",
      "unread_messages",
      "unreadMessages",
      "unread_conversations",
      "unreadConversations",
      "unread_count",
      "unreadCount",
    ]),
  };
};

export const fetchAutomationTemplates = (params) =>
  http.get(SMS_AUTOMATION_TEMPLATE_ENDPOINT, { params });

export const createAutomationFlowFromTemplate = (templateId, payload = {}) => {
  const editablePayload = withoutLifecycleFields(payload);

  return http.post(`${SMS_AUTOMATION_TEMPLATE_ENDPOINT}/${templateId}/create-flow`, {
    ...editablePayload,
    initial_message_send_time:
      editablePayload.initial_message_send_time || DEFAULT_INITIAL_MESSAGE_SEND_TIME,
  });
};

export const fetchAutomationFlows = (params) =>
  http.get(SMS_AUTOMATION_FLOW_ENDPOINT, { params });

export const fetchAutomationFlow = (flowId) =>
  http.get(`${SMS_AUTOMATION_FLOW_ENDPOINT}/${flowId}`);

export const updateAutomationFlow = (flowId, payload = {}) => {
  const editablePayload = withoutLifecycleFields(payload);

  return http.patch(`${SMS_AUTOMATION_FLOW_ENDPOINT}/${flowId}`, editablePayload);
};

export const fetchLiveEvents = (params) =>
  http.get(LIVE_EVENT_ENDPOINT, { params });

export const fetchAutomationConversationEvents = (params = {}) =>
  http.get(AUTOMATION_CONVERSATION_EVENTS_ENDPOINT, {
    params: {
      scope: "all",
      ...params,
    },
  });

export const publishAutomationFlow = (flowId) =>
  http.post(`${SMS_AUTOMATION_FLOW_ENDPOINT}/${flowId}/publish`);

export const pauseAutomationFlow = (flowId) =>
  http.post(`${SMS_AUTOMATION_FLOW_ENDPOINT}/${flowId}/pause`);

export const duplicateAutomationFlow = (flowId) =>
  http.post(`${SMS_AUTOMATION_FLOW_ENDPOINT}/${flowId}/duplicate`);

export const fetchSmsConversations = (params) =>
  http.get(SMS_CONVERSATION_ENDPOINT, { params });

export const fetchSmsNotifications = (params) =>
  fetchSmsConversations(params);

export const fetchSmsConversation = (conversationId, params) =>
  http.get(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}`, { params });

export const fetchSmsConversationMessages = (conversationId, params) =>
  http.get(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}/messages`, { params });

export const fetchConversationEvent = (eventId) =>
  http.get(`show-event/${eventId}`);

export const markSmsConversationRead = (conversationId) =>
  http.post(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}/mark-read`);

export const replyToSmsConversation = (conversationId, payload) =>
  http.post(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}/reply`, payload);

export const updateSmsConversationStatus = (conversationId, payload) =>
  http.patch(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}/status`, payload);

export const forceOptOutSmsConversation = (conversationId, payload) =>
  http.post(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}/force-opt-out`, payload);

export const addSmsConversationNote = (conversationId, payload) =>
  http.post(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}/notes`, payload);

export const deleteSmsConversation = (conversationId) =>
  http.delete(`${SMS_CONVERSATION_ENDPOINT}/${conversationId}`);

export const fetchSmsBillingOverview = (params) =>
  http.get(SMS_BILLING_OVERVIEW_ENDPOINT, { params });

export const fetchSmsDashboard = fetchSmsBillingOverview;

export const fetchSmsBillingSummary = (params) =>
  http.get(SMS_BILLING_SUMMARY_ENDPOINT, { params });

export const fetchSmsBillingRecords = (params) =>
  http.get(SMS_BILLING_RECORDS_ENDPOINT, { params });

export const fetchConversionsMonthlyOverview = (params) =>
  http.get(CONVERSIONS_MONTHLY_OVERVIEW_ENDPOINT, { params });

export const exportConversionsMonthlyOverviewCsv = (params) =>
  http.get(`${CONVERSIONS_MONTHLY_OVERVIEW_ENDPOINT}/export.csv`, {
    params,
    responseType: "blob",
  });

export const exportAutomationAnalyticsCsv = (url, params) =>
  http.get(url || `${CONVERSIONS_MONTHLY_OVERVIEW_ENDPOINT}/export.csv`, {
    params: url ? undefined : params,
    responseType: "blob",
  });

export const fetchSmsUsageLogs = (params) =>
  http.get(SMS_USAGE_LOGS_ENDPOINT, { params });

export const fetchSmsCreditBalance = (params) =>
  http.get(SMS_CREDIT_BALANCE_ENDPOINT, { params });

export const fetchSmsCreditTransactions = (params) =>
  http.get(SMS_CREDIT_TRANSACTIONS_ENDPOINT, { params });

export const fetchSmsDeliveryReports = (params) =>
  http.get(SMS_DELIVERY_REPORTS_ENDPOINT, { params });

export const smsNotificationActionsAvailable = false;
export const smsReminderActionsAvailable = false;
